import * as React from "react";

import type {
    ContentLibraryNavigationClickedV3,
    ContentLibraryTaskNavigationClickedV1,
    DestinationContentKindV1,
} from "@khanacademy/event-schemas";
import {
    publishAnalyticsEventForMinorConversion,
    usePublishAnalyticsEvent,
} from "@khan/analytics-learning-time";
import type {Standard} from "@khan/content-types";
import {useRegionOverride} from "@khan/data-region-override";
import {GuideStateApi} from "@khan/guide-state";
import {RequestInfo} from "@khan/shared-core";

import useContentPath from "./data-access/use-content-path.ts";
import type {ResolvedContentPath} from "./types/types.ts";
import {getUserInterfaceAnalyticsFields} from "./ui/use-common-analytics-fields.ts";
import {useShowCourseUnitRevamp} from "./ui/use-show-course-unit-revamp.ts";
import {getCurrentPageContentPathFields} from "./ui/utils/get-content-path-fields.ts";

// @see https://github.com/Khan/event-schemas/blob/main/events/ContentLibraryTaskNavigationClicked/v1.yml#L17-L20
type AttemptType = ContentLibraryTaskNavigationClickedV1["attemptType"];

// @see https://github.com/Khan/event-schemas/blob/main/events/ContentLibraryNavigationClicked/v3.yml#L16-L25
type NavigationElements =
    ContentLibraryNavigationClickedV3["navigationElement"];

// @see https://github.com/Khan/event-schemas/blob/main/traits/DestinationContentKind/v1.yml#L19C9-L34C17
type ContentKind =
    DestinationContentKindV1["destinationContentKind_contentKind"];

type NavigationClickedEvent = (
    navigationElement: NavigationElements,
    destinationContentKind: ContentKind,
) => void;

export const useAiGuidePracticeItemContextLoaded = () => {
    const {contentPath} = useContentPath();

    return React.useCallback(
        (taskKey: string, assessmentItemId: string) => {
            const guideState = GuideStateApi.latest();
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuidePracticeItemContextLoaded",
                eventSchemaVersion: 1,
                aiGuideNavigationId: guideState.navigation.id,
                aiGuideThreadId: guideState.threadId ?? null,
                taskKey,
                assessmentItemId,
                ...getCurrentPageContentPathFields(contentPath),
            });
        },
        [contentPath],
    );
};

/**
 * A hook that returns a publish ContentLibraryNavigationClicked event function.
 */
export const useContentLibraryNavigationClicked =
    (): NavigationClickedEvent => {
        const publishAnalyticsEvent = usePublishAnalyticsEvent();
        const sharedFields = useContentLibrarySharedAnalyticsFields();

        return React.useCallback(
            (
                navigationElement: NavigationElements,
                destinationContentKind: ContentKind,
            ) => {
                publishAnalyticsEvent({
                    eventType: "ContentLibraryNavigationClicked",
                    eventSchemaVersion: 3,
                    navigationElement,
                    destinationContentKind_contentKind: destinationContentKind,
                    ...sharedFields,
                });
            },
            [publishAnalyticsEvent, sharedFields],
        );
    };

type TaskNavigationClickedEvent = (
    destinationContentKind: ContentKind,
    isUpNextHighlighted: boolean,
    attemptType: AttemptType,
) => void;

/**
 * A hook that returns a publish ContentLibraryTaskNavigationClicked event
 * function.
 */
export const useContentLibraryTaskNavigationClicked =
    (): TaskNavigationClickedEvent => {
        const publishAnalyticsEvent = usePublishAnalyticsEvent();
        const sharedFields = useContentLibrarySharedAnalyticsFields();

        return React.useCallback(
            (
                destinationContentKind: ContentKind,
                isUpNextHighlighted: boolean,
                attemptType: AttemptType,
            ) => {
                publishAnalyticsEvent({
                    eventType: "ContentLibraryTaskNavigationClicked",
                    eventSchemaVersion: 2,
                    attemptType,
                    destinationContentKind_contentKind: destinationContentKind,
                    isUpNextHighlighted: !!isUpNextHighlighted, // force boolean
                    ...sharedFields,
                });
            },
            [publishAnalyticsEvent, sharedFields],
        );
    };

type PageViewedEvent = () => void;

/**
 * A hook that returns a publish ContentLibraryPageViewed event function.
 */
export const useContentLibraryPageViewed = (): PageViewedEvent => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const sharedFields = useContentLibrarySharedAnalyticsFields();
    const regionOverrideResult = useRegionOverride();

    return React.useCallback(() => {
        if (regionOverrideResult.status !== "success") {
            return;
        }
        const referrerHost = document.referrer.length
            ? new URL(document.referrer).hostname
            : null;
        publishAnalyticsEvent({
            eventType: "ContentLibraryPageViewed",
            eventSchemaVersion: 3,
            ...sharedFields,
            // TODO(CP-7627): A content item may have multiple StandardMapping
            // entities relating to different SetOfStandards. Each
            // SetOfStandards has a list of associated standard_regions
            // (field).
            // For region-specific content, we currently expect
            // standard_regions for any related SetOfStandards to be populated
            // with a single region code (e.g. "US-FL" for Florida-specific
            // content), but we provide no guarantee that this is the case
            // (likewise with no guarantee around having only one related
            // SetOfStandards).
            // It is likely preferable to obtain currentPageRegionCode via
            // query when needed, starting from a known region-specific content
            // item, standard set, etc., as this data is locally unavailable.
            // currentPageRegionCode has been left null here for now.
            currentPageRegionCode: null,
            referrerDomain: referrerHost,
            standardRegionUserContext_ipGeoRegionCode: RequestInfo.kaRegion,
            standardRegionUserContext_regionCodeSource:
                regionOverrideResult.regionSource,
            standardRegionUserContext_regionCodeUsed:
                regionOverrideResult.regionCode,
        });
    }, [publishAnalyticsEvent, sharedFields, regionOverrideResult]);
};

type StandardClickedEvent = (standard: Standard) => void;

/**
 * A hook that returns a publish ContentLibraryPageStandardClicked event function.
 */
export const useContentLibraryPageStandardClicked =
    (): StandardClickedEvent => {
        const publishAnalyticsEvent = usePublishAnalyticsEvent();
        const sharedFields = useContentLibrarySharedAnalyticsFields();
        const regionOverrideResult = useRegionOverride();

        return React.useCallback(
            (standard: Standard) => {
                if (regionOverrideResult.status !== "success") {
                    return;
                }
                const referrerHost = document.referrer.length
                    ? new URL(document.referrer).hostname
                    : null;
                publishAnalyticsEvent({
                    eventType: "ContentLibraryPageStandardClicked",
                    eventSchemaVersion: 1,
                    ...sharedFields,
                    referrerDomain: referrerHost,
                    targetStandardSetId: standard.set.id ?? "",
                    targetStandardId: standard.code,
                    standardRegionUserContext_ipGeoRegionCode:
                        RequestInfo.kaRegion,
                    standardRegionUserContext_regionCodeSource:
                        regionOverrideResult.regionSource,
                    standardRegionUserContext_regionCodeUsed:
                        regionOverrideResult.regionCode,
                });
            },
            [publishAnalyticsEvent, sharedFields, regionOverrideResult],
        );
    };

/**
 * A hook that returns a publish ContentLibraryPageStandardClicked event function.
 * This version of the hook takes a latestContentPath argument to support
 * usage with components that aren't wrapped in ContentPathContextProvider,
 * like <ContentHeader>.
 */
export const useContentLibraryPageStandardClickedWithContentPath = (
    latestContentPath?: ResolvedContentPath,
): StandardClickedEvent => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const regionOverrideResult = useRegionOverride();

    return React.useCallback(
        (standard: Standard) => {
            if (regionOverrideResult.status !== "success") {
                return;
            }
            const referrerHost = document.referrer.length
                ? new URL(document.referrer).hostname
                : null;
            const sharedFieldsViaSuppliedContentPath = {
                ...getCurrentPageContentPathFields(latestContentPath),
                ...getUserInterfaceAnalyticsFields(),
            };

            publishAnalyticsEvent({
                eventType: "ContentLibraryPageStandardClicked",
                eventSchemaVersion: 1,
                ...sharedFieldsViaSuppliedContentPath,
                referrerDomain: referrerHost,
                targetStandardSetId: standard.set.id ?? "",
                targetStandardId: standard.code,
                standardRegionUserContext_ipGeoRegionCode: RequestInfo.kaRegion,
                standardRegionUserContext_regionCodeSource:
                    regionOverrideResult.regionSource,
                standardRegionUserContext_regionCodeUsed:
                    regionOverrideResult.regionCode,
            });
        },
        [publishAnalyticsEvent, latestContentPath, regionOverrideResult],
    );
};

// Exported for testing only
export const _noOp = (() => {}) as any;

type ContentLibraryNavigationClickEventPublishers = {
    navigationClicked: NavigationClickedEvent;
    taskNavigationClicked: TaskNavigationClickedEvent;
    standardsClicked: StandardClickedEvent;
};

type RenderProp = (
    eventPublishers: ContentLibraryNavigationClickEventPublishers,
) => React.ReactNode;

/**
 * A render-prop component that provides ContentLibraryNavigationClicked,
 * ContentLibraryTaskNavigationClicked, and ContentLibraryPageStandardClicked publishers
 * to its child function.
 *
 * Use this when you want to publish new events from class components.
 */
export const RenderWithContentLibraryNavigationEvents = (props: {
    children: RenderProp;
}): React.ReactElement => {
    const showCUR = useShowCourseUnitRevamp();

    if (showCUR) {
        return <Internal fn={props.children} />;
    }

    return (
        <>
            {props.children({
                navigationClicked: _noOp,
                taskNavigationClicked: _noOp,
                standardsClicked: _noOp,
            })}
        </>
    );
};

const Internal = (props: {fn: RenderProp}): React.ReactElement => {
    const navigationClicked = useContentLibraryNavigationClicked();
    const taskNavigationClicked = useContentLibraryTaskNavigationClicked();
    const standardsClicked = useContentLibraryPageStandardClicked();

    return (
        <>
            {props.fn({
                navigationClicked,
                taskNavigationClicked,
                standardsClicked,
            })}
        </>
    );
};

export type WithContentLibraryNavigationEvents =
    ContentLibraryNavigationClickEventPublishers;
export type WithoutContentLibraryNavigationEvents<T> = Omit<
    T,
    keyof WithContentLibraryNavigationEvents
>;

// Copied from withMarkConversion. I think there are some type improvements
// here, but I don't know if it is worth anyone's time to make them.
export const withContentLibraryNavigationEvents = <
    C extends React.ComponentType<
        React.ComponentProps<C> & WithContentLibraryNavigationEvents
    > & {displayName?: string | undefined},
    OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof ContentLibraryNavigationClickEventPublishers
    >,
>(
    WrappedComponent: C,
): React.ComponentType<OuterProps> => {
    return (props: OuterProps) => (
        <RenderWithContentLibraryNavigationEvents>
            {(publishers) => (
                <WrappedComponent {...(props as any)} {...publishers} />
            )}
        </RenderWithContentLibraryNavigationEvents>
    );
};

/**
 * This component handles the side-effect logic for publishing a
 * ContentLibraryPageViewed event.
 *
 * Use this when you want to publish the event from a class component or when
 * otherwise helpful in an arbitrary React tree.
 */
export const ContentLibraryPageViewedEventPublisher = (): null => {
    const pageViewedEvent = useContentLibraryPageViewed();
    const lastLogged = React.useRef<string | null>(null);
    const {pathname} = location;

    React.useEffect(() => {
        if (lastLogged.current !== pathname) {
            pageViewedEvent();
            lastLogged.current = pathname;
        }
    }, [lastLogged, pageViewedEvent, pathname]);

    return null;
};

type SharedAnalyticsFields = ReturnType<
    typeof getCurrentPageContentPathFields
> &
    ReturnType<typeof getUserInterfaceAnalyticsFields>;

/**
 * A hook that returns all of the currentPageContentPath_* and
 * contentLibraryUserInterface_* analytics fields.
 */
export const useContentLibrarySharedAnalyticsFields =
    (): SharedAnalyticsFields => {
        const {contentPath} = useContentPath();

        return {
            ...getCurrentPageContentPathFields(contentPath),
            ...getUserInterfaceAnalyticsFields(),
        };
    };
